import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { EffectsLevel, TerminalAppModel } from "../state/ui-types";
import { ChevronDownIcon, CopyIcon, DisconnectIcon, RefreshIcon, SettingsIcon, WalletIcon } from "./Icons";
import { truncateHash } from "../lib/truncate-hash";
import { shortAddress } from "./format";
import { ConnectWalletModal } from "./ConnectWalletModal";
import { CreatorFeesOverlay } from "../features/launchpad/components/CreatorFeesOverlay";
import { CopyAddressButton } from "./CopyAddressButton";

interface WalletHeaderProps {
  projectToken?: { address: string; symbol: string } | null;
  navigation?: ReactNode;
  model: TerminalAppModel;
  walletPolicy?: "authenticate" | "connect-only";
  launchpadFees?: boolean;
  onConnect(): void;
  onChooseWallet?(id: string): void;
  onDisconnect(): void;
  onSwitchNetwork(): void;
  onSelectSourceToken(address: string): void;
}

export function WalletHeader({ model, projectToken, walletPolicy = "authenticate", navigation, launchpadFees = false, onConnect, onChooseWallet, onDisconnect, onSwitchNetwork, onSelectSourceToken }: WalletHeaderProps) {
  const activeToken = projectToken ?? model.activeSourceToken;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsId = useId();
  const settingsButton = useRef<HTMLButtonElement>(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [feesOpen, setFeesOpen] = useState(false);
  const [copyResult, setCopyResult] = useState<string | null>(null);
  const walletId = useId();
  const walletWrap = useRef<HTMLDivElement>(null);
  const walletButton = useRef<HTMLButtonElement>(null);
  const copyButton = useRef<HTMLButtonElement>(null);
  const copyRevision = useRef(0);
  const connected = model.wallet.address !== null;
  const busy = !connected && model.wallet.status === "CONNECTING";

  useEffect(() => {
    setWalletOpen(false);
    setCopyResult(null);
    return () => { copyRevision.current += 1; };
  }, [model.contextKey, model.wallet.address, model.wallet.status]);

  useEffect(() => {
    if (!walletOpen) return;
    copyButton.current?.focus();
    function outside(event: PointerEvent) {
      if (!walletWrap.current?.contains(event.target as Node)) setWalletOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setWalletOpen(false);
      queueMicrotask(() => walletButton.current?.focus());
    }
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [walletOpen]);

  function copyAddress() {
    const address = model.wallet.address;
    if (!address) return;
    const revision = ++copyRevision.current;
    const failed = () => {
      if (revision === copyRevision.current) setCopyResult(`Copy unavailable. Select the address to copy it. ${address}`);
    };
    try {
      navigator.clipboard.writeText(address).then(() => {
        if (revision === copyRevision.current) setCopyResult("Address copied.");
      }, failed);
    } catch {
      failed();
    }
  }

  function closeSettings() {
    setSettingsOpen(false);
    queueMicrotask(() => settingsButton.current?.focus());
  }

  return (
    <header className="app-header">
      <div className="brand-lockup" aria-label={model.brandName}>
        <img src="/logo.jpg" alt="" className="brand-mark" width="42" height="42" />
        <div>
          <p className="brand-name">{model.brandName}</p>
          <p className="brand-kicker">Proof of work · forest rewards</p>
        </div>
      </div>

      <div className="header-context-row">
        <div className="header-token" aria-label={projectToken ? "Project token" : "Active source token"}>
          <span className="field-eyebrow">{projectToken ? "PROJECT TOKEN" : "SOURCE TOKEN"}</span>
          {!projectToken && model.sourceTokens.length > 1 ? (
            <label className="token-select-wrap">
              <span className="sr-only">Choose source token</span>
              <select
                value={model.activeSourceToken?.address ?? ""}
                onChange={(event) => onSelectSourceToken(event.target.value)}
                disabled={["STARTING", "MINING", "CANDIDATE_FOUND", "SUBMITTING"].includes(model.mining.phase)}
              >
                {model.sourceTokens.map((token) => (
                  <option key={token.address} value={token.address}>{token.symbol} · {shortAddress(token.address)}</option>
                ))}
              </select>
              <ChevronDownIcon className="select-chevron" />
            </label>
          ) : (
            <div className="token-identity">
              <span className="token-dot" aria-hidden="true" />
              <strong>{activeToken?.symbol ??
                (model.runtime.backend === "OFFLINE" ? "BACKEND UNAVAILABLE" : "TOKEN NOT LAUNCHED")}</strong>
              <span title={activeToken?.address}>{activeToken ? shortAddress(activeToken.address) :
                model.runtime.backend === "OFFLINE" ? "RETRY CONNECTED API" : "WAITING FOR LAUNCH"}</span>
            </div>
          )}
          {projectToken && model.runtime.backend !== "LIVE" ? <span className="source-context" role="status">{
            model.runtime.backend === "LOADING" ? "CHECKING BACKEND" : null
            // model.runtime.backend === "OFFLINE" ? "BACKEND UNAVAILABLE" : "BACKEND DEGRADED"
          }</span> : null}
          {activeToken ? <CopyAddressButton key={activeToken.address} address={activeToken.address} label={projectToken ? "project token address" : "source token address"} /> : null}
        </div>

        {navigation}
      </div>

      <div className="header-actions">
        <button
          ref={settingsButton}
          type="button"
          className="icon-button"
          aria-label="Effects and display settings"
          aria-expanded={settingsOpen}
          aria-controls={settingsId}
          onClick={() => setSettingsOpen((open) => !open)}
        >
          <SettingsIcon />
        </button>
        <div className="wallet-control" ref={walletWrap} onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setWalletOpen(false);
        }}>
          <div className="wallet-action-group">
            {model.wallet.authState === "logout-pending" || model.wallet.authState === "logout-failed" ? (
              <button ref={walletButton} type="button" className="wallet-button primary"
                disabled={model.wallet.authState === "logout-pending"}
                aria-busy={model.wallet.authState === "logout-pending" || undefined} onClick={onDisconnect}>
                <DisconnectIcon /><span className="wallet-copy"><span className="wallet-label">
                  {model.wallet.authState === "logout-pending" ? "Logging out…" : "Retry logout"}
                </span></span>
              </button>
            ) : busy ? (
              <button type="button" className="wallet-button primary" disabled aria-busy="true">
                <WalletIcon />
                <span className="wallet-copy">
                  <span className="wallet-label">Connecting…</span>
                </span>
              </button>
            ) : connected ? (
              <button ref={walletButton} type="button" className="wallet-button connected"
                aria-label={`Wallet options: ${shortAddress(model.wallet.address)} ${model.wallet.chainLabel}`}
                aria-haspopup="dialog" aria-expanded={walletOpen} aria-controls={walletId}
                onClick={() => { setSettingsOpen(false); setWalletOpen((open) => !open); }}>
                <span className="wallet-presence" aria-hidden="true" />
                <span className="wallet-copy">
                  <span className="wallet-address">{shortAddress(model.wallet.address)}</span>{" "}
                  <span className="wallet-chain">{model.wallet.chainLabel}</span>
                </span>
              </button>
            ) : (
              <button
                ref={walletButton}
                type="button"
                className="wallet-button primary"
                aria-haspopup="dialog"
                aria-expanded={model.walletSelection?.isOpen ?? false}
                onClick={() => {
                  setSettingsOpen(false);
                  if (model.actions.openWalletSelection) model.actions.openWalletSelection();
                  else onConnect();
                }}
              >
                <WalletIcon />
                <span className="wallet-copy"><span className="wallet-label">Connect wallet</span></span>
              </button>
            )}

          </div>
          {walletOpen && model.wallet.address ? <div id={walletId} className="wallet-popover" role="dialog" aria-label="Wallet menu">
            <div className="wallet-menu-heading"><strong>Wallet</strong><button type="button" className="text-button" onClick={() => {
              setWalletOpen(false); queueMicrotask(() => walletButton.current?.focus());
            }}>Close</button></div>
            <dl><div><dt>Address</dt><dd className="wallet-menu-address"><span title={model.wallet.address}>{truncateHash(model.wallet.address)}</span>
              <button ref={copyButton} type="button" className="copy-address-button" aria-label="Copy address" title="Copy address" onClick={copyAddress}><CopyIcon /></button>
              {launchpadFees && <button type="button" className="wallet-creator-fees" onClick={() => {
                walletButton.current?.focus(); setWalletOpen(false); setFeesOpen(true);
              }}>Creator fees</button>}</dd></div>
              <div><dt>Chain</dt><dd className="wallet-menu-chain"><span>{model.wallet.chainLabel}</span>
                {model.wallet.status === "WRONG_NETWORK" ? <button type="button" className="wallet-switch-chain" onClick={onSwitchNetwork}><RefreshIcon />Switch chain</button> : null}</dd></div></dl>
            <div className="wallet-menu-actions">
              <button type="button" className="danger-action" onClick={() => { setWalletOpen(false); onDisconnect(); }}><DisconnectIcon />Disconnect</button>
            </div>
            {copyResult ? <p role="status">{copyResult}</p> : null}
          </div> : null}
        </div>

      </div>

      {feesOpen && launchpadFees && <CreatorFeesOverlay onClose={() => {
        setFeesOpen(false); queueMicrotask(() => walletButton.current?.focus());
      }} />}
      {model.walletSelection ? <ConnectWalletModal selection={model.walletSelection}
        demo={model.environment === "DEMO"} authenticate={walletPolicy === "authenticate"}
        onClose={() => model.actions.closeWalletSelection?.()}
        onConnect={(id) => onChooseWallet ? onChooseWallet(id) : onConnect()} /> : null}

      {settingsOpen ? (
        <div id={settingsId} className="settings-popover" role="dialog" aria-label="Display settings">
          <div className="settings-topbar">
            <div className={`environment-badge environment-${model.environment.toLowerCase()}`} title={model.environmentNote}>
              <span className="status-led" aria-hidden="true" />
              {model.environmentLabel}
            </div>
            <button type="button" className="text-button" onClick={closeSettings}>Close</button>
          </div>
          <div className="settings-heading">
            <div>
              <strong>Forest effects</strong>
              <span>Mining automatically favors lighter effects.</span>
            </div>
          </div>
          <div className="segmented-control" aria-label="Effects level">
            {(["ON", "LOW", "OFF"] as const).map((level: EffectsLevel) => (
              <button
                type="button"
                key={level}
                aria-pressed={model.effects === level}
                onClick={() => model.setEffects(level)}
              >
                {level}
              </button>
            ))}
          </div>
          <p className="settings-note">Reduced-motion preferences always override decorative movement.</p>
          {model.sound ? <div className="settings-sound">
            <div className="settings-heading">
              <div>
                <strong>Sound effect</strong>
                <span>Ancient stone, quiet resonance, and a chime for verified proofs.</span>
              </div>
            </div>
            <div className="segmented-control" role="group" aria-label="Mining sound">
              <button type="button" aria-label="Sound on" aria-pressed={model.sound.enabled}
                onClick={() => model.sound?.setEnabled(true)}>ON</button>
              <button type="button" aria-label="Sound off" aria-pressed={!model.sound.enabled}
                onClick={() => model.sound?.setEnabled(false)}>OFF</button>
            </div>
            <p className="settings-note">Mute anytime without pausing mining. Sound is independent of forest effects.</p>
          </div> : null}
        </div>
      ) : null}
    </header>
  );
}
